import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Select, SelectChangeEvent, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, FormControl, InputLabel, Divider, FormHelperText } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React from "react";

type Template = { id: string; name: string; category: 'RFQ' | 'Booking' | 'Payment' | 'Delivery Tracking'; title: string; body: string };
type Trigger = { id: string; event: string; templateId: string; enabled: boolean };
type LogItem = { id: string; time: string; channel: 'PUSH'; category: Template['category']; title: string; status: 'SUCCESS' | 'FAILED' };

const CATEGORIES: Template['category'][] = ['RFQ', 'Booking', 'Payment', 'Delivery Tracking'];

const FAKE_TEMPLATES: Template[] = [
  { id: 't1', name: 'RFQ Approved', category: 'RFQ', title: 'Your RFQ is approved', body: 'RFQ {{rfqId}} has been approved.' },
  { id: 't2', name: 'Booking Confirmed', category: 'Booking', title: 'Booking Confirmed', body: 'Booking {{bookingId}} is confirmed for {{dateTime}}.' },
  { id: 't3', name: 'Payment Failed', category: 'Payment', title: 'Payment Failed', body: 'Your payment for booking {{bookingId}} failed.' },
];

const FAKE_TRIGGERS: Trigger[] = [
  { id: 'g1', event: 'Booking Confirmed', templateId: 't2', enabled: true },
  { id: 'g2', event: 'Payment Failed', templateId: 't3', enabled: true },
];

const FAKE_LOGS: LogItem[] = [
  { id: 'l1', time: '2025-11-17 10:12', channel: 'PUSH', category: 'Booking', title: 'Booking Confirmed', status: 'SUCCESS' },
  { id: 'l2', time: '2025-11-17 10:01', channel: 'PUSH', category: 'Payment', title: 'Payment Failed', status: 'FAILED' },
  { id: 'l3', time: '2025-11-16 17:42', channel: 'PUSH', category: 'RFQ', title: 'RFQ Approved', status: 'SUCCESS' },
];

const ManageNotifications = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useState<'TEMPLATES' | 'TRIGGERS' | 'LOGS'>('TEMPLATES');
  const [searchTerm, setSearchTerm] = useState("");

  // Templates
  const [templates, setTemplates] = useState<Template[]>(FAKE_TEMPLATES);
  const [catFilter, setCatFilter] = useState<string>('');
  const [tplMenuEl, setTplMenuEl] = useState<null | HTMLElement>(null);
  const [tplMenuFor, setTplMenuFor] = useState<string | null>(null);

  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplForm, setTplForm] = useState<Template>({ id: '', name: '', category: 'RFQ', title: '', body: '' });

  // Triggers
  const [triggers, setTriggers] = useState<Trigger[]>(FAKE_TRIGGERS);
  const [trgDialogOpen, setTrgDialogOpen] = useState(false);
  const [trgForm, setTrgForm] = useState<Trigger>({ id: '', event: '', templateId: '', enabled: true });

  // Logs
  const [logs] = useState<LogItem[]>(FAKE_LOGS);

  const filteredTemplates = templates.filter((t) =>
    (!catFilter || t.category === catFilter) &&
    (!searchTerm || `${t.name} ${t.title} ${t.body}`.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const openTplMenu = (e: React.MouseEvent<HTMLElement>, id: string) => { setTplMenuEl(e.currentTarget); setTplMenuFor(id); };
  const closeTplMenu = () => { setTplMenuEl(null); setTplMenuFor(null); };

  const openCreateTemplate = () => { setTplForm({ id: '', name: '', category: 'RFQ', title: '', body: '' }); setTplDialogOpen(true); };
  const openEditTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setTplForm({ ...t });
    setTplDialogOpen(true);
  };
  const saveTemplate = () => {
    if (!tplForm.name.trim()) return;
    if (tplForm.id) setTemplates((prev) => prev.map((t) => (t.id === tplForm.id ? tplForm : t)));
    else setTemplates((prev) => [...prev, { ...tplForm, id: `t${prev.length + 1}` }]);
    setTplDialogOpen(false);
  };
  const deleteTemplate = (id: string) => { setTemplates((prev) => prev.filter((t) => t.id !== id)); };

  const openCreateTrigger = () => { setTrgForm({ id: '', event: '', templateId: templates[0]?.id || '', enabled: true }); setTrgDialogOpen(true); };
  const saveTrigger = () => {
    if (trgForm.id) setTriggers((prev) => prev.map((t) => (t.id === trgForm.id ? trgForm : t)));
    else setTriggers((prev) => [...prev, { ...trgForm, id: `g${prev.length + 1}` }]);
    setTrgDialogOpen(false);
  };

  const successCount = logs.filter((l) => l.status === 'SUCCESS').length;
  const failureCount = logs.filter((l) => l.status === 'FAILED').length;

  // Helpers
  const insertVar = (token: string) => {
    setTplForm((prev) => ({ ...prev, body: (prev.body ? prev.body + ' ' : '') + token }));
  };

  return (
    <>
      <div className="main_title">
        <h1>Notification & Communication Center</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Notification & Communication Center
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="custom_tabs1" sx={{ width: '100%', mb: 1 }}>
              <Tabs value={tab === 'TEMPLATES' ? 0 : tab === 'TRIGGERS' ? 1 : 2} onChange={(_, i) => setTab(i === 0 ? 'TEMPLATES' : i === 1 ? 'TRIGGERS' : 'LOGS')} className="custom_tabs_links">
                <Tab label="Templates" />
                <Tab label="Triggers" />
                <Tab label="Logs" />
              </Tabs>
            </Box>
            <SearchBar searchTerm={searchTerm} setDebouncedSearchTerm={setSearchTerm} placeholder={tab === 'TEMPLATES' ? 'Search templates...' : tab === 'TRIGGERS' ? 'Search triggers...' : 'Search logs...'} />
          </div>
          <div className="right_s">
            {tab === 'TEMPLATES' && (
              <Button className="btn btn_primary" onClick={openCreateTemplate}><AddIcon /> New Template</Button>
            )}
            {tab === 'TRIGGERS' && (
              <Button className="btn btn_primary" onClick={openCreateTrigger}><AddIcon /> New Trigger</Button>
            )}
          </div>
        </div>

        {tab === 'TEMPLATES' && (
          <>
            <div className="form" style={{ marginBottom: 12 }}>
              <div className="control_group" style={{ maxWidth: 260 }}>
                <Select fullWidth displayEmpty value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
                  <MenuItem value="">All Categories</MenuItem>
                  {CATEGORIES.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
                </Select>
              </div>
            </div>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTemplates.length ? filteredTemplates.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.name}</TableCell>
                      <TableCell>{t.category}</TableCell>
                      <TableCell>{t.title}</TableCell>
                      <TableCell>
                        <IconButton onClick={(e) => openTplMenu(e, t.id)}><MoreVertIcon /></IconButton>
                        <Menu anchorEl={tplMenuEl} open={Boolean(tplMenuEl) && tplMenuFor === t.id} onClose={closeTplMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                          <MenuItem onClick={() => { openEditTemplate(t.id); closeTplMenu(); }}>Edit</MenuItem>
                          <MenuItem onClick={() => { deleteTemplate(t.id); closeTplMenu(); }}>Delete</MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={4}>No templates found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {tab === 'TRIGGERS' && (
          <>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {triggers.length ? triggers.map((g) => {
                    const tpl = templates.find((t) => t.id === g.templateId);
                    return (
                      <TableRow key={g.id} hover>
                        <TableCell>{g.event}</TableCell>
                        <TableCell>{tpl ? tpl.name : '-'}</TableCell>
                        <TableCell>{g.enabled ? <Chip size="small" color="success" label="Enabled" /> : <Chip size="small" label="Disabled" />}</TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow><TableCell colSpan={3}>No triggers defined</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {tab === 'LOGS' && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Chip size="small" color="success" label={`Success: ${successCount}`} />
              <Chip size="small" color="error" label={`Failed: ${failureCount}`} />
            </Box>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.filter((l) => !searchTerm || `${l.title} ${l.category}`.toLowerCase().includes(searchTerm.toLowerCase())).map((l) => (
                    <TableRow key={l.id} hover>
                      <TableCell>{l.time}</TableCell>
                      <TableCell>{l.channel}</TableCell>
                      <TableCell>{l.category}</TableCell>
                      <TableCell>{l.title}</TableCell>
                      <TableCell>{l.status === 'SUCCESS' ? <Chip size="small" color="success" label="Success" /> : <Chip size="small" color="error" label="Failed" />}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </div>

      {/* Create/Edit Template Dialog */}
      <Dialog
        open={tplDialogOpen}
        onClose={() => setTplDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            backgroundColor: '#fff',
            boxShadow: '0 30px 60px rgba(15,23,42,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', pb: 0 }}>
          {tplForm.id ? 'Edit Template' : 'New Template'}
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5, pb: 2.5 }}>
          <FormHelperText sx={{ mb: 2, color: '#94a3b8' }}>
            Configure the content that will be sent to users.
          </FormHelperText>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.25 }}>
      {/* Name Field */}
      <TextField 
        size="small" 
        label="Name" 
        placeholder="e.g. Booking Confirmed" 
        value={tplForm.name} 
        onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} 
        fullWidth 
        InputLabelProps={{ shrink: true }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
            transition: 'all 0.2s ease',
            '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
            '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' }
          }
        }}
      />

      {/* Category Dropdown */}
      <FormControl fullWidth size="small">
        <InputLabel sx={{ fontSize: '0.875rem' }}>Category</InputLabel>
        <Select 
          label="Category" 
          value={tplForm.category} 
          onChange={(e) => setTplForm({ ...tplForm, category: e.target.value as Template['category'] })}
          sx={{
            borderRadius: 1.5,
            transition: 'all 0.2s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e2e8f0'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': { 
              borderColor: '#cbd5e1'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
            }
          }}
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Title Field */}
      <TextField 
        size="small" 
        label="Title" 
        placeholder="Short headline shown in notification" 
        value={tplForm.title} 
        onChange={(e) => setTplForm({ ...tplForm, title: e.target.value })} 
        fullWidth 
        sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, '& .MuiOutlinedInput-root': {
          borderRadius: 1.5,
          transition: 'all 0.2s ease',
          '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
          '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' }
        } }}
        InputLabelProps={{ shrink: true }}
      />

      {/* Body Field */}
      <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
        <TextField
          size="small"
          label="Message Body"
          placeholder="Write the message. You can use variables like {{bookingId}} and {{dateTime}}"
          value={tplForm.body}
          onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })}
          fullWidth
          multiline
          minRows={7}
          InputLabelProps={{ shrink: true }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              transition: 'all 0.2s ease',
              backgroundColor: '#fff',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
              '&.Mui-focused': { 
                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                borderColor: '#3b82f6'
              }
            },
            '& textarea': { 
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              padding: '10px'
            }
          }}
        />
        <FormHelperText sx={{ mt: 1, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
          Use variables to personalize messages
        </FormHelperText>
      </Box>
    </Box>

    {/* Variables Section */}
    <Box sx={{ 
      mt: 3, 
      pt: 2,
      borderTop: '1px dashed #e2e8f0'
    }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Available Variables
        </span>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { label: '{{rfqId}}', icon: '📋' },
          { label: '{{bookingId}}', icon: '📅' },
          { label: '{{dateTime}}', icon: '⏰' },
          { label: '{{amount}}', icon: '💰' }
        ].map((v) => (
          <Chip 
            key={v.label}
            size="small" 
            label={`${v.icon} ${v.label}`}
            variant="outlined" 
            clickable 
            onClick={() => insertVar(v.label)}
            sx={{
              borderRadius: 1,
              borderColor: '#cbd5e1',
              color: '#475569',
              fontFamily: 'ui-monospace',
              fontWeight: 600,
              fontSize: '0.8rem',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.05)',
                color: '#3b82f6',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
              }
            }}
          />
        ))}
      </Box>
    </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5,
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            borderRadius: '0 0 16px 16px'
          }}
        >
          <Button 
            onClick={() => setTplDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#64748b',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(100, 116, 139, 0.08)',
                color: '#334155'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={saveTemplate} 
            disabled={!tplForm.name.trim()}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: 'none',
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' }
            }}
          >
            {tplForm.id ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Trigger Dialog */}
      <Dialog
        open={trgDialogOpen}
        onClose={() => setTrgDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            backgroundColor: '#fff',
            boxShadow: '0 25px 50px rgba(15,23,42,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', pb: 0 }}>
          New Trigger
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <FormHelperText sx={{ mb: 2, color: '#94a3b8' }}>
            Pair an event with the template users should receive.
          </FormHelperText>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
            <TextField
              label="Event"
              placeholder="e.g. Booking Confirmed"
              value={trgForm.event}
              onChange={(e) => setTrgForm({ ...trgForm, event: e.target.value })}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Template</InputLabel>
              <Select
                label="Template"
                value={trgForm.templateId}
                onChange={(e) => setTrgForm({ ...trgForm, templateId: e.target.value as string })}
              >
                {templates.map((t) => (<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            borderRadius: '0 0 16px 16px'
          }}
        >
          <Button onClick={() => setTrgDialogOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            onClick={saveTrigger}
            disabled={!trgForm.event || !trgForm.templateId}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: 'none',
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' }
            }}
          >
            Save Trigger
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageNotifications;
